import { useEffect, useMemo, useRef, useState } from 'react'
import { resolveContinentCountries } from '@/views/worldView/continentMapping'
import { continentToCountries, CONTINENT_NAMES, REGION_LABEL } from '@/constants'
import type { WorldPoint } from '@/analysis/apiTypes'

type CentroidMap = Record<string, { x: number; y: number }>

interface Args {
  svgRef: React.RefObject<SVGSVGElement | null>
  points: WorldPoint[]
  dimension: string
  agg: any
}

// Build a stable signature for the inputs that actually affect centroids.
// `points` is a new array on every stream tick, but the centroid only depends
// on the country codes per point id. We hash that, not the array identity.
function pointsCentroidKey(points: WorldPoint[], dimension: string): string {
  const parts: string[] = [dimension]
  for (const p of points) {
    const codes =
      (p.extra as any)?.countryCodes
      ?? (p.extra as any)?.countryCodesByContinent
      ?? null
    // For continent/country dimensions, points typically do not include countryCodes in `extra`.
    // We still want a stable signature based on id/label so the effect runs when the node set changes.
    const id = String((p as any)?.id ?? '')
    const label = String((p as any)?.label ?? '')
    parts.push(`${id}:${label}:${codes ? JSON.stringify(codes) : ''}`)
  }
  return parts.join('|')
}

function normContinentDisplayName(idOrLabel: string): string | null {
  const raw = String(idOrLabel ?? '').trim()
  if (!raw) return null
  const up = raw.toUpperCase()
  if (REGION_LABEL[up as keyof typeof REGION_LABEL]) return REGION_LABEL[up as keyof typeof REGION_LABEL]!
  if (CONTINENT_NAMES.includes(raw)) return raw
  const hit = CONTINENT_NAMES.find((n) => n.toLowerCase() === raw.toLowerCase())
  return hit ?? null
}

// Computes a centroid (x, y) per point id by measuring the bounding box of
// the relevant country <path> elements in the SVG. Runs after paint, only
// when the country-set composition actually changes.
export function useCentroids({ svgRef, points, dimension, agg }: Args): CentroidMap {
  const [centroids, setCentroids] = useState<CentroidMap>({})
  const codesByCon = useMemo(
    () => resolveContinentCountries(agg?.continentCountries),
    [agg?.continentCountries],
  )

  // Stable signature: only re-run getBBox when the country composition changes,
  // not on every liveStreams tick.
  const sig = useMemo(() => pointsCentroidKey(points, dimension), [points, dimension])
  const lastSig = useRef<string>('')

  useEffect(() => {
    const svg = svgRef.current
    if (!svg) return
    if (sig === lastSig.current) return

    const bboxCache = new Map<string, DOMRect>()
    const getBBoxForCode = (cc: string): DOMRect | null => {
      const code = cc.toUpperCase()
      const cached = bboxCache.get(code)
      if (cached) return cached
      const el = svg.querySelector(`#${CSS.escape(code)}`) as SVGGraphicsElement | null
      if (!el) return null
      try {
        const b = el.getBBox()
        bboxCache.set(code, b)
        return b
      } catch {
        return null
      }
    }

    const raf = requestAnimationFrame(() => {
      const next: CentroidMap = {}
      for (const p of points) {
        let codes: string[] | null = null
        const extraCodes = (p.extra as any)?.countryCodes
        const extraByCon = (p.extra as any)?.countryCodesByContinent as Record<string, string[]> | undefined

        if (Array.isArray(extraCodes)) {
          codes = extraCodes.filter((x: any) => typeof x === 'string')
        } else if (dimension === 'country') {
          const cc = String(p.id ?? '').trim().toUpperCase()
          codes = (cc && cc.length === 2) ? [cc] : null
        } else if (dimension === 'continent') {
          const key = String((p as any)?.id ?? (p as any)?.label ?? '').trim()
          const disp = normContinentDisplayName(key)
          codes = disp ? continentToCountries(disp) : null
        } else if (dimension === 'region' && typeof p.id === 'string') {
          const raw = String(p.id).trim()
          const key = raw === 'OT' || raw === 'Oceania' ? 'OC' : raw.toUpperCase()
          // Demo-friendly rule: if region id looks like a country code, center it on that country.
          if (key.length === 2) {
            codes = [key]
          } else {
            // If region is actually a continent code/display name, center on the continent bbox union.
            const disp = normContinentDisplayName(key)
            codes = disp ? continentToCountries(disp) : (codesByCon[key] ?? codesByCon[raw] ?? null)
          }
        }

        // Fallback: if this point carries a representative country code (common for non-geo dimensions),
        // center the marker on that country shape.
        if ((!codes || !codes.length) && typeof (p as any)?.country === 'string') {
          const cc = String((p as any).country).trim().toUpperCase()
          if (cc && cc.length === 2) codes = [cc]
        }

        // For studio/stream points spanning multiple continents, center using dominant continent.
        // Prefer continentCounts (stream-weighted) over unique-country count (set length).
        if (extraByCon && typeof extraByCon === 'object') {
          const counts = (p.extra as any)?.continentCounts as Record<string, number> | undefined
          let bestKey: string | null = null
          let bestScore = -1
          for (const [k, v] of Object.entries(extraByCon)) {
            const score = typeof counts?.[k] === 'number'
              ? Number(counts[k])
              : (Array.isArray(v) ? v.length : 0)
            if (score > bestScore) { bestScore = score; bestKey = k }
          }
          if (bestKey && Array.isArray(extraByCon[bestKey]) && extraByCon[bestKey].length) {
            codes = extraByCon[bestKey]
          }
        }
        if (!codes || !codes.length) continue

        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
        let hit = 0
        for (const cc of codes) {
          const b = getBBoxForCode(cc)
          if (!b) continue
          hit += 1
          minX = Math.min(minX, b.x)
          minY = Math.min(minY, b.y)
          maxX = Math.max(maxX, b.x + b.width)
          maxY = Math.max(maxY, b.y + b.height)
        }
        if (!hit) continue
        next[p.id] = { x: (minX + maxX) / 2, y: (minY + maxY) / 2 }
      }
      lastSig.current = sig
      setCentroids(next)
    })

    return () => cancelAnimationFrame(raf)
  }, [sig, points, dimension, codesByCon, svgRef])

  return centroids
}
